import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import axios from 'axios';
import SuperAdminLayout from '../../../layouts/SuperAdminLayout';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from '../../../utils/api';

const Ledger = () => {
  const user = JSON.parse(localStorage.getItem('user'));
  const superAdminId = user?.id;

  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [ledgerData, setLedgerData] = useState([]);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [openingType, setOpeningType] = useState('');

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const res = await api.get(`/api/customers/get-all/${superAdminId}`);
        setCustomers(res.data);
      } catch (err) {
        console.error('Failed to fetch customers:', err);
      }
    };

    fetchCustomers();
  }, [superAdminId]);

  useEffect(() => {
    const fetchOpeningBalance = async () => {
      if (!selectedCustomer) return;

      try {
        const res = await api.get(`/api/customers/get-all/${superAdminId}`);
        const cust = res.data.find((c) => c.partyName === selectedCustomer);
        if (cust) {
          setOpeningBalance(cust.openingBalance || 0);
          setOpeningType(cust.openingBalanceType || '');
        }
      } catch (err) {
        console.error('Error fetching opening balance:', err);
      }
    };

    fetchOpeningBalance();
  }, [selectedCustomer]);

  useEffect(() => {
    const fetchLedger = async () => {
      if (!selectedCustomer) return;

      try {
        const res = await api.get(
          `/api/ledger/party/${superAdminId}/${selectedCustomer}`
        );
        setLedgerData(res.data);
      } catch (err) {
        console.error('Failed to fetch ledger:', err);
      }
    };

    fetchLedger();
  }, [selectedCustomer]);

  // Calculate totals
  let totalDebit = openingType === 'debit' ? openingBalance : 0;
  let totalCredit = openingType === 'credit' ? openingBalance : 0;

  ledgerData.forEach((entry) => {
    totalDebit += entry.debit || 0;
    totalCredit += entry.credit || 0;
  });

  const closingDebit = totalCredit > totalDebit ? totalCredit - totalDebit : 0;
  const closingCredit = totalDebit > totalCredit ? totalDebit - totalCredit : 0;

  const downloadPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text('Ledger Report', 14, 15);

    const tableData = [];

    // Opening balance row
    tableData.push([
      '-',
      'Opening Balance',
      '-',
      openingType === 'debit' ? openingBalance.toFixed(2) : '-',
      openingType === 'credit' ? openingBalance.toFixed(2) : '-',
    ]);

    // Ledger entries
    ledgerData.forEach((row) => {
      tableData.push([
        row.date?.slice(0, 10) || '',
        row.type || '',
        row.challanNumber || '',
        row.debit > 0 ? row.debit.toFixed(2) : '-',
        row.credit > 0 ? row.credit.toFixed(2) : '-',
      ]);
    });

    tableData.push([
      '',
      '',
      'Closing Balance',
      closingDebit > 0 ? closingDebit.toFixed(2) : '-',
      closingCredit > 0 ? closingCredit.toFixed(2) : '-',
    ]);

    tableData.push([
      '',
      '',
      'Total',
      (totalDebit + closingDebit).toFixed(2),
      (totalCredit + closingCredit).toFixed(2),
    ]);

    autoTable(doc, {
      startY: 25,
      head: [
        ['Date', 'Voucher Type', 'Challan No', 'Debit (Dr)', 'Credit (Cr)'],
      ],
      body: tableData,
      theme: 'striped',
      styles: { fontSize: 10 },
      headStyles: { fillColor: [22, 160, 133] },
    });

    doc.save('ledger-report.pdf');
  };

  return (
    <SuperAdminLayout>
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          Party Ledger
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <TextField
            select
            label="Party Name"
            value={selectedCustomer}
            onChange={(e) => setSelectedCustomer(e.target.value)}
            fullWidth
            margin="dense"
          >
            {customers.map((party) => (
              <MenuItem key={party._id} value={party.partyName}>
                {party.partyName}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Button variant="outlined" sx={{ mb: 2 }} onClick={downloadPDF}>
          Download PDF
        </Button>

        {ledgerData.length > 0 && (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f4f4f4' }}>
                  <TableCell>Date</TableCell>
                  <TableCell>Voucher Type</TableCell>
                  <TableCell>Challan No</TableCell>
                  <TableCell>Debit (Dr)</TableCell>
                  <TableCell>Credit (Cr)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Opening balance row */}
                <TableRow>
                  <TableCell>-</TableCell>
                  <TableCell>Opening Balance</TableCell>
                  <TableCell>-</TableCell>
                  <TableCell>
                    {openingType === 'debit' ? openingBalance.toFixed(2) : '-'}
                  </TableCell>
                  <TableCell>
                    {openingType === 'credit' ? openingBalance.toFixed(2) : '-'}
                  </TableCell>
                </TableRow>

                {ledgerData.map((entry, index) => (
                  <TableRow key={index}>
                    <TableCell>{entry.date?.slice(0, 10)}</TableCell>
                    <TableCell>{entry.type}</TableCell>
                    <TableCell>{entry.challanNumber}</TableCell>
                    <TableCell>
                      {entry.debit ? entry.debit.toFixed(2) : '-'}
                    </TableCell>
                    <TableCell>
                      {entry.credit ? entry.credit.toFixed(2) : '-'}
                    </TableCell>
                  </TableRow>
                ))}

                <TableRow
                  sx={{ backgroundColor: '#f0f0f0', fontWeight: 'bold' }}
                >
                  <TableCell colSpan={3}>Closing Balance</TableCell>
                  <TableCell>
                    {closingDebit > 0 ? closingDebit.toFixed(2) : '-'}
                  </TableCell>
                  <TableCell>
                    {closingCredit > 0 ? closingCredit.toFixed(2) : '-'}
                  </TableCell>
                </TableRow>

                <TableRow
                  sx={{ backgroundColor: '#e0e0e0', fontWeight: 'bold' }}
                >
                  <TableCell colSpan={3}>Total</TableCell>
                  <TableCell>
                    {(totalDebit + closingDebit).toFixed(2)}
                  </TableCell>
                  <TableCell>
                    {(totalCredit + closingCredit).toFixed(2)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>
    </SuperAdminLayout>
  );
};

export default Ledger;
